import { useState, useMemo } from 'react';
import { useTabNavigationStore } from '../../store/tabNavigationStore';
import { useAuthStore } from '../../store/authStore';
import { createOrganization } from '../../api/organizationApi';
import { formatPrice } from '../../constants/tiers';
import { useSubscriptionTiersByType } from '../../hooks/useSubscriptionTiers';
import type { SubscriptionTier } from '../../types/organization';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { PageHeader } from '../../components/ui/PageHeader';

const TIER_DESCRIPTIONS: Record<string, string> = {
  team: 'Для небольших команд',
  club: 'Для спортивных клубов',
  academy: 'Для академий и школ',
  enterprise: 'Для крупных организаций',
};

export function CreateOrganizationScreen() {
  const { popScreen } = useTabNavigationStore();
  const switchToOrganization = useAuthStore((state) => state.switchToOrganization);
  const user = useAuthStore((state) => state.user);

  const [name, setName] = useState('');
  const [tier, setTier] = useState<SubscriptionTier>('team');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data: tiers, isLoading: isLoadingTiers } = useSubscriptionTiersByType('organization');

  const tierMap = useMemo(() => {
    if (!tiers) return {};
    return tiers.reduce((acc, t) => {
      acc[t.tier] = t;
      return acc;
    }, {} as Record<string, (typeof tiers)[0]>);
  }, [tiers]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!user?.id) {
      setError('Не удалось определить текущего пользователя');
      return;
    }
    if (!name.trim()) {
      setError('Введите название организации');
      return;
    }
    if (name.length > 200) {
      setError('Название слишком длинное (макс. 200 символов)');
      return;
    }

    try {
      setLoading(true);
      const org = await createOrganization({ user_id: user.id, name: name.trim(), tier });
      await switchToOrganization(org.id);

      const tierName = tierMap[tier]?.name || tier.toUpperCase();
      const trialDays = tier === 'team' ? 7 : 14; // TEAM: 7 days, others: 14 days
      alert(`Организация "${org.name}" создана! Вы получили ${trialDays} дней бесплатного триала тарифа ${tierName}.`);
      popScreen();
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Не удалось создать организацию');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '10px 12px', borderRadius: '10px',
    border: '1px solid var(--separator)', backgroundColor: 'var(--bg-primary)',
    color: 'var(--text-primary)', fontSize: '17px', outline: 'none',
  };
  const labelStyle: React.CSSProperties = {
    display: 'block', fontSize: '13px', color: 'var(--text-hint)', marginBottom: '4px',
  };

  if (isLoadingTiers) {
    return <div className="flex items-center justify-center h-full"><p style={{ color: 'var(--text-hint)' }}>Загрузка тарифов...</p></div>;
  }

  if (!tiers || tiers.length === 0) {
    return (
      <div className="p-4 space-y-4">
        <Card>
          <p className="text-center py-8" style={{ color: 'var(--text-hint)' }}>Не удалось загрузить тарифные планы</p>
        </Card>
        <Button onClick={() => popScreen()} variant="secondary" fullWidth>&larr; Назад</Button>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4 pb-24">
      <PageHeader
        title="Создание организации"
        subtitle="Выберите тарифный план и укажите название"
      />

      {error && (
        <Card>
          <p className="text-sm" style={{ color: 'var(--danger)' }}>{error}</p>
        </Card>
      )}

      {/* Tier Selection */}
      <div className="space-y-3">
        <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Выберите тарифный план:</p>

        {tiers.map((tierData) => {
          const tierKey = tierData.tier as SubscriptionTier;
          const isSelected = tier === tierKey;
          const priceDisplay = tierData.price_monthly === 0 ? 'По запросу' : formatPrice(tierData.price_monthly);
          const membersCount = tierData.max_members || 0;

          return (
            <div
              key={tierKey}
              onClick={() => setTier(tierKey)}
              className="cursor-pointer rounded-xl p-4 transition-all"
              style={{
                backgroundColor: 'var(--bg-secondary)',
                border: isSelected ? '2px solid var(--accent)' : '2px solid transparent',
              }}
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-[17px] font-semibold" style={{ color: isSelected ? 'var(--accent)' : 'var(--text-primary)' }}>
                      {tierData.name}
                    </span>
                    {tierKey === 'team' && (
                      <span className="text-xs px-2 py-0.5 rounded" style={{ backgroundColor: '#22c55e', color: '#fff' }}>
                        Популярный
                      </span>
                    )}
                  </div>
                  <p className="text-sm mt-1" style={{ color: 'var(--text-hint)' }}>
                    {TIER_DESCRIPTIONS[tierKey] || ''}
                  </p>
                  <div className="flex items-center gap-4 mt-2">
                    <span className="text-sm" style={{ color: 'var(--text-primary)' }}>
                      {membersCount === -1 ? '\u221E' : `До ${membersCount}`} участников
                    </span>
                    <span className="text-sm font-medium" style={{ color: 'var(--accent)' }}>
                      {priceDisplay}
                    </span>
                  </div>
                </div>
                <div
                  className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{
                    border: isSelected ? '2px solid var(--accent)' : '2px solid var(--text-hint)',
                    backgroundColor: isSelected ? 'var(--accent)' : 'transparent',
                  }}
                >
                  {isSelected && <div className="w-2 h-2 bg-white rounded-full" />}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Trial Notice */}
      <Card>
        <p className="text-sm text-center" style={{ color: 'var(--accent)' }}>
          Бесплатный триал! Вы получите полный доступ ко всем функциям выбранного тарифа.
        </p>
        <p className="text-xs text-center mt-1" style={{ color: 'var(--text-hint)' }}>
          TEAM: 7 дней • CLUB/PRO_TEAM: 14 дней
        </p>
      </Card>

      {/* Organization Name */}
      <Card>
        <h3 className="text-[17px] font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>Название организации</h3>
        <div>
          <label style={labelStyle}>Название</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Например: Футбольная академия Спартак"
            disabled={loading}
            maxLength={200}
            style={inputStyle}
          />
          <p className="text-xs mt-1" style={{ color: 'var(--text-hint)' }}>Максимум 200 символов</p>
        </div>
      </Card>

      {/* Actions */}
      <div className="flex gap-3">
        <Button onClick={() => popScreen()} variant="secondary" fullWidth disabled={loading}>
          Назад
        </Button>
        <Button onClick={handleSubmit} disabled={loading || !name.trim()} fullWidth>
          {loading ? 'Создание...' : 'Создать организацию'}
        </Button>
      </div>
    </div>
  );
}
